/**
 * @desc 复写处理 router
 * @time 2023-07-31 15:44:05
 * @user simxin
 */

import { usePortalStore } from '@src/store';
import { loadLink } from '@src/utils/sandbox/helper';
import { parseSouce, parseRouterParam } from '@src/utils/parser';

/**
 * @desc 全局当前活跃的 mod
 */
let MOD: Mod = null!;
const activeMod = async (mod: Mod, expectedPathname: string) => {
  // 提取路径部分（忽略查询参数和hash）
  const currentPath = window.location.pathname;
  const expectedPath = expectedPathname.split('?')[0].split('#')[0];
  
  // 检查当前路径是否还匹配期望的路径（忽略查询参数）
  if (currentPath !== expectedPath) {
    console.log('路径已变化，跳过渲染:', {
      current: currentPath,
      expected: expectedPath,
      originalExpected: expectedPathname
    });
    return;
  }
  
  console.log('渲染：', expectedPath, '-', currentPath)
  const { setLoadedApp } = usePortalStore.getState();
  MOD = mod;
  MOD?.render();
  setLoadedApp(true);
};

/**
 * @desc handle history
 */
const rewrite = (type: 'pushState' | 'replaceState') => {
  const origin = history[type];
  return function (...args: any) {
    const extra = {
      prev: window.location.pathname,
      next: args[2],
    };
    const event = new CustomEvent(`on:${type}`, {
      detail: extra,
    });
    origin.apply(window.history, args);
    window.dispatchEvent(event);
  };
};

/**
 * @desc 应用加载逻辑
 */
const rerender = async (pathname: string, prevpath?: string) => {
  if (!/^\//.test(pathname)) return;
  const { app, chat, menu, submenu } = await parseRouterParam(pathname, true);

  if (prevpath) {
    /**
     * @desc 一级路由没变则不需要重新渲染
     */
    const prev = await parseRouterParam(prevpath, true);
    if (prev.menu === menu) {
      /**
       * @desc 二级跳回一级需要重新渲染
       */
      if (!(prev.submenu && !submenu)) {
        return;
      }
    }
  }
  /**
   * @desc 写入应用类型
   */
  const { setApp, setMenu } = usePortalStore.getState();
  setApp(app);
  setMenu(chat === 'chat' ? 'chat' : menu);

  /**
   * @desc 判断是否为 chat
   */
  const { setIsChat } = usePortalStore.getState();
  setIsChat(chat === 'chat');

  /**
   * @desc 构造 mod 路径
   */
  let action = menu || 'index';
  let module = `modules/${app}/${action}/${action}`;
  /**
   * @desc chat mod 特殊处理
   */
  if (chat === 'chat') {
    /**
     * @desc chat 官网控制台主页屏蔽展示
     */
    // action = menu || 'detail';
    module = `modules/chat/${action}/${action}`;
  }

  /**
   * @desc 先卸载原 mod
   */
  const { setLoadedApp } = usePortalStore.getState();
  setLoadedApp(false);
  if (MOD) MOD?.destroy();

  if (seajs.defined.includes(module)) {
    seajs.use(module, (mod: Mod) => {
      activeMod(mod, pathname);
    });
  } else {
    /**
     * @desc 保证样式加载完成后再渲染
     */
    const { js, css } = await parseSouce(app, chat, menu, submenu);
    Promise.all([
      ...css.map(
        (e, i) =>
          new Promise((resolve, reject) => {
            loadLink(`'micro-app-css-link-${i}'`, e, ret => {
              ret ? resolve(true) : reject();
            });
          }),
      ),
      ...js.map(
        e =>
          new Promise((resolve, reject) => {
            seajs.request(e, ret => {
              ret ? resolve(true) : reject();
            });
          }),
      ),
    ])
      .then(() => {
        seajs.use(module, (mod: Mod) => {
          activeMod(mod, pathname);
        });
      })
      .catch(() => {
        const { setErrorApp } = usePortalStore.getState();
        setErrorApp(true);
      });
  }
};

/**
 * @desc 监听 history
 * push/replace 重写过，不需要监听原生事件
 */
export const router = () => {
  history.pushState = rewrite('pushState');
  history.replaceState = rewrite('replaceState');

  window.addEventListener('on:replaceState', (e: any) => {
    // console.log('>>>>>>> replaceState');
    document.body.dispatchEvent(
      new CustomEvent('router:re', {
        detail: { router: e.detail.next },
      }),
    );
    rerender(e.detail.next, e.detail.prev);
  });
  window.addEventListener('on:pushState', (e: any) => {
    // console.log('>>>>>>> pushState');
    document.body.dispatchEvent(
      new CustomEvent('router:re', {
        detail: { router: e.detail.next },
      }),
    );
    rerender(e.detail.next, e.detail.prev);
  });
  window.addEventListener('popstate', () => {
    // console.log('>>>>>>> popstate');
    document.body.dispatchEvent(
      new CustomEvent('router:re', {
        detail: { router: window.location.pathname },
      }),
    );
    rerender(window.location.pathname);
  });
  rerender(window.location.pathname);
};
